import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';

import { NumericField } from '@paragon/material';
import { naturalSortByProperty } from '@paragon/utilities';

import { LayoutModelsLibraryState } from '../store';

interface ComponentQuantityListProps {
  readonly quantities: { [guid: string]: number };
  readonly setCustomQuantity: (guid: string, quantity: number) => void;
  /**
   * Optional function to get the name of the component by the GUID from `quantities`. If not provided, `quantities` is
   * assumed to be keyed by ComponentDesign GUID and the name is looked up from the `layoutModels` state.
   */
  readonly getName?: (guid: string) => string;
  readonly disabled?: boolean;
}

export function ComponentQuantityList({
  quantities,
  setCustomQuantity,
  getName,
  disabled,
}: ComponentQuantityListProps) {
  const layoutModelByGuid = useSelector((state: LayoutModelsLibraryState) => state.layoutModels);

  const [quantitiesShowOnlyNonZeroQuantities, setQuantitiesShowOnlyNonZeroQuantities] = useState<boolean>(
    Object.values(quantities).some((quantity) => quantity !== 0),
  );
  const quantitiesFiltered = quantitiesShowOnlyNonZeroQuantities
    ? Object.fromEntries(Object.entries(quantities).filter(([_, quantity]) => quantity !== 0))
    : quantities;
  const sortedStateQuantityEntries = naturalSortByProperty(Object.entries(quantitiesFiltered), ([guid]) =>
    getName ? getName(guid) : layoutModelByGuid[guid].name,
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Quantities
      </Typography>

      <Box sx={{ height: { xs: '250px', lg: '450px' }, overflow: 'auto', mb: 1 }}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {sortedStateQuantityEntries.map(([guid, quantity]) => (
                <TableRow
                  sx={
                    !quantitiesShowOnlyNonZeroQuantities && quantity > 0
                      ? { backgroundColor: (theme) => theme.palette.action.hover }
                      : undefined
                  }
                  key={guid}
                >
                  <TableCell>
                    <Typography variant="h6" color={quantity === 0 ? 'text.secondary' : undefined}>
                      {getName ? getName(guid) : layoutModelByGuid[guid].name}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <NumericField
                      sx={{ width: '10ch' }}
                      label="Quantity"
                      value={quantity}
                      integers
                      min={0}
                      onSubmit={(quantity) => setCustomQuantity(guid, quantity)}
                      disabled={disabled}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => void setQuantitiesShowOnlyNonZeroQuantities((oldState) => !oldState)}
        disabled={Object.values(quantities).every((quantity) => quantity > 0)}
      >
        {quantitiesShowOnlyNonZeroQuantities ? 'Show All' : 'Filter to non-zero quantities'}
      </Button>
    </Box>
  );
}
